<template>
  <Dialog :commonDialog="dialog" :dialogWidth="520">
    <template v-slot:title>
      <div
        class="d-flex align-center justify-space-between w-100 export-dialog-header"
      >
        <div class="d-flex align-center">
          <div class="export-header-icon mr-3">
            <v-icon color="cyan" size="22">mdi-database-export</v-icon>
          </div>
          <div>
            <div class="export-header-title">{{ title }}</div>
            <div class="export-header-subtitle">
              Configure export settings and delivery
            </div>
          </div>
        </div>
        <v-btn
          icon
          small
          class="export-header-close-btn"
          :disabled="exportLoading"
          v-on:click="closeDialog"
          title="Close"
        >
          <v-icon size="18" color="grey darken-1">mdi-close</v-icon>
        </v-btn>
      </div>
    </template>
    <template v-slot:body>
      <v-form ref="exportForm" v-model="formValid" class="export-dialog-form">
        <!-- Email Field Section -->
        <div class="export-form-section mb-4">
          <label for="export-email" class="export-field-label">
            Email Address <span class="export-required-star">*</span>
          </label>
          <v-text-field
            id="export-email"
            v-model.trim="form.email"
            placeholder="Enter recipient email"
            type="email"
            autocomplete="email"
            dense
            filled
            solo
            flat
            color="cyan"
            prepend-inner-icon="mdi-email"
            hide-details="auto"
            class="export-input-field"
            :rules="[
              validateRules.required(form.email, 'Email'),
              validateRules.validEmail(form.email, 'Email'),
            ]"
            required
          ></v-text-field>
          <div class="export-input-hint mt-1 d-flex align-center">
            <v-icon x-small color="grey darken-1" class="mr-1"
              >mdi-information-outline</v-icon
            >
            <span>The exported file will be sent to this email address.</span>
          </div>
        </div>

        <!-- Date Scope Section -->
        <div class="export-form-section">
          <label class="export-field-label">
            Date Scope <span class="export-required-star">*</span>
          </label>

          <v-radio-group
            v-model="form.dateOption"
            class="ma-0 pa-0 export-date-radio-group"
            hide-details
            row
          >
            <v-row class="ma-0 w-100">
              <v-col cols="12" sm="6" class="pa-1">
                <div
                  class="export-option-card"
                  :class="{
                    'is-selected': form.dateOption === 'current_month',
                  }"
                  @click="form.dateOption = 'current_month'"
                >
                  <v-radio
                    value="current_month"
                    color="cyan"
                    class="ma-0 pa-0"
                    hide-details
                  >
                    <template v-slot:label>
                      <div class="export-option-body ml-2">
                        <div class="export-option-title">Current Month</div>
                        <div class="export-option-desc">
                          Export current month records
                        </div>
                      </div>
                    </template>
                  </v-radio>
                </div>
              </v-col>

              <v-col cols="12" sm="6" class="pa-1">
                <div
                  class="export-option-card"
                  :class="{ 'is-selected': form.dateOption === 'date_range' }"
                  @click="form.dateOption = 'date_range'"
                >
                  <v-radio
                    value="date_range"
                    color="cyan"
                    class="ma-0 pa-0"
                    hide-details
                  >
                    <template v-slot:label>
                      <div class="export-option-body ml-2">
                        <div class="export-option-title">Custom Date Range</div>
                        <div class="export-option-desc">
                          Choose start & end dates
                        </div>
                      </div>
                    </template>
                  </v-radio>
                </div>
              </v-col>
            </v-row>
          </v-radio-group>

          <!-- Custom Date Range Picker -->
          <v-expand-transition>
            <div
              v-if="form.dateOption === 'date_range'"
              class="export-custom-date-container mt-3"
            >
              <div class="export-date-picker-label mb-1">
                <span>Select Date Interval</span>
                <span class="export-required-star ml-1">*</span>
              </div>
              <DateRangePicker
                v-model="form.dates"
                label="From Date - To Date"
                class="export-date-range-picker"
                v-on:click:clear="form.dates = []"
              ></DateRangePicker>
              <div
                v-if="dateRangeError"
                class="v-messages error--text mt-2 d-flex align-center"
                role="alert"
              >
                <v-icon x-small color="error" class="mr-1"
                  >mdi-alert-circle-outline</v-icon
                >
                <span>Please select both From Date and To Date.</span>
              </div>
            </div>
          </v-expand-transition>
        </div>
      </v-form>
    </template>
    <template v-slot:action>
      <div class="d-flex align-center justify-end w-100 pa-1">
        <v-btn
          depressed
          class="export-action-cancel mx-2"
          :disabled="exportLoading"
          v-on:click="closeDialog"
        >
          Cancel
        </v-btn>
        <v-btn
          depressed
          color="cyan"
          class="export-action-submit mx-2 white--text"
          v-on:click="submit"
          :loading="exportLoading"
          :disabled="exportLoading"
        >
          <v-icon left small>mdi-database-export</v-icon>
          Export
        </v-btn>
      </div>
    </template>
  </Dialog>
</template>

<script>
import moment from "moment-timezone";
import ValidationMixin from "@/core/plugins/validation-mixin";
import ApiService from "@/core/services/api.service";
import { currentUser } from "@/core/services/jwt.service";
import Dialog from "@/view/pages/partials/Dialog.vue";
import DateRangePicker from "@/view/components/DateRangePicker";

export default {
  name: "ExportDialog",
  components: {
    Dialog,
    DateRangePicker,
  },
  mixins: [ValidationMixin],
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      default: "Export",
    },
    module: {
      type: String,
      required: true,
    },
    queryParams: {
      type: Object,
      default: () => ({}),
    },
    listingSearch: {
      type: Object,
      default: () => ({}),
    },
    initialEmail: {
      type: String,
      default: "",
    },
    isClosedInvoice: {
      type: [Boolean, Number, String],
      default: null,
    },
    isPartiallyInstalled: {
      type: [Boolean, Number, String],
      default: null,
    },
  },
  data() {
    return {
      formValid: false,
      dateRangeError: false,
      exportLoading: false,
      form: {
        email: "",
        dateOption: "current_month",
        dates: [],
      },
    };
  },
  watch: {
    dialog(value) {
      if (value) {
        const loggedInUser = currentUser();
        this.dateRangeError = false;
        this.form.email = loggedInUser.user_email || this.initialEmail;
        this.$nextTick(() => {
          if (this.$refs.exportForm) {
            this.$refs.exportForm.resetValidation();
          }
        });
      }
    },
    "form.dateOption"() {
      this.dateRangeError = false;
    },
  },

  methods: {
    closeDialog() {
      this.dateRangeError = false;
      this.$emit("close");
    },
    submit() {
      const formIsValid = this.$refs.exportForm.validate();
      this.dateRangeError =
        this.form.dateOption === "date_range" &&
        (!this.form.dates || this.form.dates.length !== 2);

      if (!formIsValid || this.dateRangeError) {
        return;
      }

      const dates =
        this.form.dateOption === "current_month"
          ? [
              moment().startOf("month").format("YYYY-MM-DD"),
              moment().endOf("month").format("YYYY-MM-DD"),
            ]
          : this.form.dates;

      this.exportLoading = true;
      ApiService.query("export/common", {
        ...this.queryParams,
        ...this.listingSearch,
        module_type: this.module,
        email: this.form.email,
        start_date: dates[0],
        end_date: dates[1],
        is_closed_invoice: this.isClosedInvoice ? 1 : null,
        is_partially_installed: this.isPartiallyInstalled ? 1 : null,
      })
        .then(() => {
          this.$emit("close");
        })
        .finally(() => {
          this.exportLoading = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
.export-dialog-header {
  padding: 4px 0;
  width: 100%;

  .export-header-icon {
    width: 38px;
    height: 38px;
    min-width: 38px;
    border-radius: 8px;
    background: rgba(0, 188, 212, 0.1);
    border: 1px solid rgba(0, 188, 212, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .export-header-title {
    font-size: 20px !important;
    font-weight: 700 !important;
    color: #24326d !important;
    line-height: 1.25 !important;
    letter-spacing: 0.3px !important;
  }

  .export-header-subtitle {
    font-size: 12px !important;
    font-weight: 400 !important;
    color: #7e8299 !important;
    line-height: 1.3 !important;
    margin-top: 2px !important;
    letter-spacing: normal !important;
  }

  .export-header-close-btn {
    transition: background-color 0.2s ease;
    border-radius: 6px;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
}

.export-dialog-form {
  padding: 4px 0;

  .export-form-section {
    position: relative;
  }

  .export-field-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #24326d;
    margin-bottom: 6px;
    letter-spacing: 0.2px;
  }

  .export-required-star {
    color: #ed1c24;
    font-weight: 700;
    margin-left: 2px;
  }

  .export-input-hint {
    font-size: 12px;
    color: #7e8299;
    line-height: 1.4;
  }

  .export-date-radio-group {
    width: 100%;

    ::v-deep .v-input__control,
    ::v-deep .v-input__slot {
      width: 100%;
    }
  }

  .export-option-card {
    height: 100%;
    border: 1.5px solid #e2e5ec;
    border-radius: 8px;
    padding: 10px 12px;
    background-color: #fafbfc;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    user-select: none;

    &:hover {
      border-color: #00bcd4;
      background-color: #f4fbfb;
    }

    &.is-selected {
      border-color: #00bcd4;
      background-color: #f0fbfc;
      box-shadow: 0 2px 8px rgba(0, 188, 212, 0.15);

      .export-option-title {
        color: #00838f;
      }
    }

    .export-option-body {
      line-height: 1.3;
    }

    .export-option-title {
      font-size: 13px;
      font-weight: 600;
      color: #24326d;
      transition: color 0.2s ease;
    }

    .export-option-desc {
      font-size: 11px;
      color: #7e8299;
      margin-top: 2px;
      line-height: 1.25;
    }

    ::v-deep .v-radio {
      width: 100%;
      margin: 0 !important;

      .v-label {
        width: 100%;
        cursor: pointer;
        margin: 0;
      }
    }
  }

  .export-custom-date-container {
    background: #f8fafc;
    border: 1px dashed #dcdfe6;
    border-radius: 8px;
    padding: 12px 14px;

    .export-date-picker-label {
      font-size: 12px;
      font-weight: 600;
      color: #24326d;
    }
  }
}

/* Custom text field styling */
::v-deep .export-input-field {
  .v-input__slot {
    border: 1px solid #bec1c7 !important;
    border-radius: 4px !important;
    background: #ffffff !important;
    min-height: 40px !important;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  &:hover .v-input__slot {
    border-color: #00bcd4 !important;
  }

  &.v-input--is-focused .v-input__slot {
    border-color: #00bcd4 !important;
    box-shadow: 0 0 0 1px #00bcd4 !important;
  }

  &.error--text .v-input__slot {
    border-color: #ed1c24 !important;
  }

  .v-input__prepend-inner {
    margin-top: 8px !important;
  }

  input {
    font-size: 13px !important;
    color: #24326d !important;
  }
}

/* Date picker inside container */
::v-deep .export-date-range-picker {
  .v-text-field--solo .v-input__slot {
    border: 1px solid #e2e5ec !important;
    border-radius: 6px !important;
    background: #ffffff !important;
    min-height: 40px !important;
    box-shadow: none !important;
    transition: border-color 0.2s ease;

    &:hover {
      border-color: #00bcd4 !important;
    }
  }

  .v-text-field--solo.v-input--is-focused .v-input__slot {
    border-color: #00bcd4 !important;
    box-shadow: 0 0 0 1px #00bcd4 !important;
  }
}

/* Action buttons */
.export-action-cancel {
  border: 1px solid #e2e5ec !important;
  background-color: #ffffff !important;
  color: #5e6278 !important;
  border-radius: 6px !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  height: 36px !important;
  padding: 0 18px !important;
  letter-spacing: 0.3px;
  text-transform: capitalize !important;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f8fa !important;
    color: #24326d !important;
    border-color: #d1d5db !important;
  }
}

.export-action-submit {
  border-radius: 6px !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  height: 36px !important;
  padding: 0 20px !important;
  letter-spacing: 0.3px;
  text-transform: capitalize !important;
  box-shadow: 0 2px 8px rgba(0, 188, 212, 0.28) !important;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 188, 212, 0.38) !important;
  }
}
</style>
